/**
 * useExpenseForm Hook
 * Reusable form hook using useReducer for Expense forms
 */

import { useReducer, useRef, useCallback } from "react";
import {
  expenseFormReducer,
  initialExpenseFormState,
  EXPENSE_FORM_ACTION_TYPES,
} from "../utils/expenseFormReducer";
import {
  validateDescription,
  validateDate,
  validateCategory,
  validateCurrency,
  validateSubTotal,
  validateTax,
  validateUser,
  validateClient,
  validateCompany,
  validateFileAttachment,
  validateExpenseForm,
} from "../utils/expenseValidators";

/**
 * Custom hook for Expense form management
 * @param {Object} initialValues - Initial form values (optional)
 * @returns {Object} Form state and handlers
 */
export const useExpenseForm = (initialValues = {}) => {
  // Initialize state with custom values if provided
  const initialState = {
    ...initialExpenseFormState,
    ...initialValues,
  };

  const [formState, dispatch] = useReducer(expenseFormReducer, initialState);

  // Refs for focus management
  const descriptionRef = useRef(null);
  const dateRef = useRef(null);
  const categoryRef = useRef(null);
  const currencyRef = useRef(null);
  const subTotalRef = useRef(null);
  const taxRef = useRef(null);
  const userRef = useRef(null);
  const clientRef = useRef(null);
  const companyRef = useRef(null);

  // Field Setters
  const setDescription = useCallback((description) => {
    dispatch({ type: EXPENSE_FORM_ACTION_TYPES.SET_DESCRIPTION, payload: description });
  }, []);

  const setExpenseDate = useCallback((date) => {
    dispatch({ type: EXPENSE_FORM_ACTION_TYPES.SET_EXPENSE_DATE, payload: date });
  }, []);

  const setCategory = useCallback((category) => {
    dispatch({ type: EXPENSE_FORM_ACTION_TYPES.SET_CATEGORY, payload: category });
  }, []);

  const setCurrency = useCallback((currency) => {
    dispatch({ type: EXPENSE_FORM_ACTION_TYPES.SET_CURRENCY, payload: currency });
  }, []);

  const setSubTotal = useCallback((subTotal) => {
    dispatch({ type: EXPENSE_FORM_ACTION_TYPES.SET_SUB_TOTAL, payload: subTotal });
  }, []);

  const setTax = useCallback((tax) => {
    dispatch({ type: EXPENSE_FORM_ACTION_TYPES.SET_TAX, payload: tax });
  }, []);

  const setAmount = useCallback((amount) => {
    dispatch({ type: EXPENSE_FORM_ACTION_TYPES.SET_AMOUNT, payload: amount });
  }, []);

  const setIsBillable = useCallback((isBillable) => {
    dispatch({ type: EXPENSE_FORM_ACTION_TYPES.SET_IS_BILLABLE, payload: isBillable });
  }, []);

  const setUser = useCallback((user) => {
    dispatch({ type: EXPENSE_FORM_ACTION_TYPES.SET_USER, payload: user });
  }, []);

  const setClient = useCallback((client) => {
    dispatch({ type: EXPENSE_FORM_ACTION_TYPES.SET_CLIENT, payload: client });
  }, []);

  const setProject = useCallback((project) => {
    dispatch({ type: EXPENSE_FORM_ACTION_TYPES.SET_PROJECT, payload: project });
  }, []);

  const setCompany = useCallback((company) => {
    dispatch({ type: EXPENSE_FORM_ACTION_TYPES.SET_COMPANY, payload: company });
  }, []);

  const setNote = useCallback((note) => {
    dispatch({ type: EXPENSE_FORM_ACTION_TYPES.SET_NOTE, payload: note });
  }, []);

  const setFileAttachment = useCallback((files) => {
    dispatch({ type: EXPENSE_FORM_ACTION_TYPES.SET_FILE_ATTACHMENT, payload: files });
  }, []);

  const setFileAttachmentName = useCallback((name) => {
    dispatch({ type: EXPENSE_FORM_ACTION_TYPES.SET_FILE_ATTACHMENT_NAME, payload: name });
  }, []);

  // Dropdown Search Term Setters
  const setCurrencySearchTerm = useCallback((term) => {
    dispatch({ type: EXPENSE_FORM_ACTION_TYPES.SET_CURRENCY_SEARCH_TERM, payload: term });
  }, []);

  const setUserSearchTerm = useCallback((term) => {
    dispatch({ type: EXPENSE_FORM_ACTION_TYPES.SET_USER_SEARCH_TERM, payload: term });
  }, []);

  const setClientSearchTerm = useCallback((term) => {
    dispatch({ type: EXPENSE_FORM_ACTION_TYPES.SET_CLIENT_SEARCH_TERM, payload: term });
  }, []);

  const setProjectSearchTerm = useCallback((term) => {
    dispatch({ type: EXPENSE_FORM_ACTION_TYPES.SET_PROJECT_SEARCH_TERM, payload: term });
  }, []);

  const setCompanySearchTerm = useCallback((term) => {
    dispatch({ type: EXPENSE_FORM_ACTION_TYPES.SET_COMPANY_SEARCH_TERM, payload: term });
  }, []);

  // Dropdown Open State Setters
  const setCurrencyDropdownOpen = useCallback((isOpen) => {
    dispatch({ type: EXPENSE_FORM_ACTION_TYPES.SET_CURRENCY_DROPDOWN_OPEN, payload: isOpen });
  }, []);

  const setCategoryDropdownOpen = useCallback((isOpen) => {
    dispatch({ type: EXPENSE_FORM_ACTION_TYPES.SET_CATEGORY_DROPDOWN_OPEN, payload: isOpen });
  }, []);

  const setUserDropdownOpen = useCallback((isOpen) => {
    dispatch({ type: EXPENSE_FORM_ACTION_TYPES.SET_USER_DROPDOWN_OPEN, payload: isOpen });
  }, []);

  const setClientDropdownOpen = useCallback((isOpen) => {
    dispatch({ type: EXPENSE_FORM_ACTION_TYPES.SET_CLIENT_DROPDOWN_OPEN, payload: isOpen });
  }, []);

  const setProjectDropdownOpen = useCallback((isOpen) => {
    dispatch({ type: EXPENSE_FORM_ACTION_TYPES.SET_PROJECT_DROPDOWN_OPEN, payload: isOpen });
  }, []);

  const setCompanyDropdownOpen = useCallback((isOpen) => {
    dispatch({ type: EXPENSE_FORM_ACTION_TYPES.SET_COMPANY_DROPDOWN_OPEN, payload: isOpen });
  }, []);

  // Validation Setters
  const setDescriptionValid = useCallback((isValid) => {
    dispatch({ type: EXPENSE_FORM_ACTION_TYPES.SET_DESCRIPTION_VALID, payload: isValid });
  }, []);

  const setDateValid = useCallback((isValid) => {
    dispatch({ type: EXPENSE_FORM_ACTION_TYPES.SET_DATE_VALID, payload: isValid });
  }, []);

  const setCategoryValid = useCallback((isValid) => {
    dispatch({ type: EXPENSE_FORM_ACTION_TYPES.SET_CATEGORY_VALID, payload: isValid });
  }, []);

  const setCurrencyValid = useCallback((isValid) => {
    dispatch({ type: EXPENSE_FORM_ACTION_TYPES.SET_CURRENCY_VALID, payload: isValid });
  }, []);

  const setSubTotalValid = useCallback((isValid) => {
    dispatch({ type: EXPENSE_FORM_ACTION_TYPES.SET_SUB_TOTAL_VALID, payload: isValid });
  }, []);

  const setTaxValid = useCallback((isValid) => {
    dispatch({ type: EXPENSE_FORM_ACTION_TYPES.SET_TAX_VALID, payload: isValid });
  }, []);

  const setUserValid = useCallback((isValid) => {
    dispatch({ type: EXPENSE_FORM_ACTION_TYPES.SET_USER_VALID, payload: isValid });
  }, []);

  const setClientValid = useCallback((isValid) => {
    dispatch({ type: EXPENSE_FORM_ACTION_TYPES.SET_CLIENT_VALID, payload: isValid });
  }, []);

  const setCompanyValid = useCallback((isValid) => {
    dispatch({ type: EXPENSE_FORM_ACTION_TYPES.SET_COMPANY_VALID, payload: isValid });
  }, []);

  // Validation Functions
  const validateField = useCallback((fieldName, value, additionalData = {}) => {
    let isValid = false;
    let error = null;

    switch (fieldName) {
      case "description":
        isValid = validateDescription(value);
        if (!isValid) {
          setDescriptionValid(true);
          descriptionRef.current?.focus();
        }
        break;
      case "date":
        isValid = validateDate(value);
        if (!isValid) {
          setDateValid(true);
          dateRef.current?.focus();
        }
        break;
      case "category":
        isValid = validateCategory(value);
        if (!isValid) {
          setCategoryValid(true);
          categoryRef.current?.focus();
        }
        break;
      case "currency":
        isValid = validateCurrency(value);
        if (!isValid) {
          setCurrencyValid(true);
          currencyRef.current?.focus();
        }
        break;
      case "subTotal":
        isValid = validateSubTotal(value);
        if (!isValid) {
          setSubTotalValid(true);
          subTotalRef.current?.focus();
        }
        break;
      case "tax":
        isValid = validateTax(value);
        if (!isValid) {
          setTaxValid(true);
          taxRef.current?.focus();
        }
        break;
      case "user":
        isValid = validateUser(value);
        if (!isValid) {
          setUserValid(true);
          userRef.current?.focus();
        }
        break;
      case "client":
        isValid = validateClient(value);
        if (!isValid) {
          setClientValid(true);
          clientRef.current?.focus();
        }
        break;
      case "company":
        isValid = validateCompany(value);
        if (!isValid) {
          setCompanyValid(true);
          companyRef.current?.focus();
        }
        break;
      case "fileAttachment":
        const fileValidation = validateFileAttachment(value);
        isValid = fileValidation.isValid;
        error = fileValidation.error;
        break;
      default:
        isValid = true;
    }

    return { isValid, error };
  }, [
    setDescriptionValid,
    setDateValid,
    setCategoryValid,
    setCurrencyValid,
    setSubTotalValid,
    setTaxValid,
    setUserValid,
    setClientValid,
    setCompanyValid,
  ]);

  // Complete Form Validation
  const validateForm = useCallback(() => {
    // Reset all validation states
    dispatch({ type: EXPENSE_FORM_ACTION_TYPES.RESET_VALIDATION });

    const validationResult = validateExpenseForm(formState);
    
    // Set validation states for invalid fields
    if (!validationResult.isValid) {
      const validationState = {};
      
      if (validationResult.errors.includes("description")) {
        validationState.descriptionIsValid = true;
        descriptionRef.current?.focus();
      }
      if (validationResult.errors.includes("date")) {
        validationState.dateIsValid = true;
        dateRef.current?.focus();
      }
      if (validationResult.errors.includes("category")) {
        validationState.categoryIsValid = true;
        categoryRef.current?.focus();
      }
      if (validationResult.errors.includes("currency")) {
        validationState.currencyIsValid = true;
        currencyRef.current?.focus();
      }
      if (validationResult.errors.includes("subTotal")) {
        validationState.subTotalIsValid = true;
        subTotalRef.current?.focus();
      }
      if (validationResult.errors.includes("tax")) {
        validationState.taxIsValid = true;
        taxRef.current?.focus();
      }
      if (validationResult.errors.includes("user")) {
        validationState.userIsValid = true;
        userRef.current?.focus();
      }
      if (validationResult.errors.includes("client")) {
        validationState.clientIsValid = true;
        clientRef.current?.focus();
      }
      if (validationResult.errors.includes("company")) {
        validationState.companyIsValid = true;
        companyRef.current?.focus();
      }
      
      if (Object.keys(validationState).length > 0) {
        dispatch({ type: EXPENSE_FORM_ACTION_TYPES.SET_VALIDATION_STATE, payload: validationState });
      }
    }

    return validationResult;
  }, [formState]);

  // Reset Form
  const resetForm = useCallback(() => {
    dispatch({ type: EXPENSE_FORM_ACTION_TYPES.RESET_FORM });
  }, []);

  // Reset Validation
  const resetValidation = useCallback(() => {
    dispatch({ type: EXPENSE_FORM_ACTION_TYPES.RESET_VALIDATION });
  }, []);

  // Set Form Data (bulk update)
  const setFormData = useCallback((data) => {
    dispatch({ type: EXPENSE_FORM_ACTION_TYPES.SET_FORM_DATA, payload: data });
  }, []);

  return {
    // Form State
    formState,
    
    // Field Setters
    setDescription,
    setExpenseDate,
    setCategory,
    setCurrency,
    setSubTotal,
    setTax,
    setAmount,
    setIsBillable,
    setUser,
    setClient,
    setProject,
    setCompany,
    setNote,
    setFileAttachment,
    setFileAttachmentName,
    
    // Dropdown Search Term Setters
    setCurrencySearchTerm,
    setUserSearchTerm,
    setClientSearchTerm,
    setProjectSearchTerm,
    setCompanySearchTerm,
    
    // Dropdown Open State Setters
    setCurrencyDropdownOpen,
    setCategoryDropdownOpen,
    setUserDropdownOpen,
    setClientDropdownOpen,
    setProjectDropdownOpen,
    setCompanyDropdownOpen,
    
    // Validation Setters
    setDescriptionValid,
    setDateValid,
    setCategoryValid,
    setCurrencyValid,
    setSubTotalValid,
    setTaxValid,
    setUserValid,
    setClientValid,
    setCompanyValid,
    
    // Refs
    descriptionRef,
    dateRef,
    categoryRef,
    currencyRef,
    subTotalRef,
    taxRef,
    userRef,
    clientRef,
    companyRef,
    
    // Methods
    validateField,
    validateForm,
    resetForm,
    resetValidation,
    setFormData,
  };
};
